import type {
  CoachEngagement,
  EngagementListItem,
  Match,
  MatchSummary,
  Player,
  PlayerGoal,
} from "@/lib/types";
import {
  type PlayerResponseSignal,
} from "@/features/feedback/responseSignals";

export type CoachFeedbackBoardLaneId =
  | "needs_touch"
  | "follow_up"
  | "shared"
  | "drafting";

export type CoachFeedbackBoardCardTone =
  | "default"
  | "accent"
  | "good"
  | "flag";

export type CoachFeedbackBoardAction =
  | {
      kind: "roster";
      playerId?: string;
      context?:
        | "need_help"
        | "tried_this"
        | "no_recent_feedback"
        | "unreviewed_feedback"
      | "stale_goal";
    }
  | { kind: "notes"; itemId?: number }
  | { kind: "clips"; itemId?: number }
  | { kind: "recaps"; itemId?: number; matchId?: string }
  | { kind: "summaries"; matchId?: string };

export type CoachFeedbackBoardCard = {
  id: string;
  lane: CoachFeedbackBoardLaneId;
  title: string;
  meta: string;
  description: string;
  tone: CoachFeedbackBoardCardTone;
  playerId?: string;
  action: CoachFeedbackBoardAction;
};

export type CoachFeedbackBoardLane = {
  id: CoachFeedbackBoardLaneId;
  title: string;
  description: string;
  cards: CoachFeedbackBoardCard[];
};

export type CoachFeedbackBoardSummary = {
  needsTouch: number;
  followUp: number;
  unreviewed: number;
  missingRecaps: number;
};

export type CoachFeedbackBoardModel = {
  lanes: CoachFeedbackBoardLane[];
  summary: CoachFeedbackBoardSummary;
};

export type BuildCoachFeedbackBoardInput = {
  players: Player[];
  engagement?: CoachEngagement;
  matches: Match[];
  summaries: MatchSummary[];
  goals?: PlayerGoal[];
};

const MAX_CARDS_PER_LANE = 6;

export function buildCoachFeedbackBoard({
  players,
  engagement,
  matches,
  summaries,
  goals = [],
}: BuildCoachFeedbackBoardInput): CoachFeedbackBoardModel {
  const activePlayers = players.filter((p) => p.active);
  const summaryMatchIds = new Set(summaries.map((summary) => summary.match_id));
  const activePlayersById = new Map(activePlayers.map((p) => [p.id, p]));

  const needsTouchCards = (engagement?.players_with_no_recent_feedback ?? [])
    .slice(0, MAX_CARDS_PER_LANE)
    .map((row): CoachFeedbackBoardCard => {
      const label = playerLabel({
        display_name: row.display_name,
        jersey_number: row.jersey_number,
      });
      return {
        id: `no-recent-${row.player_id}`,
        lane: "needs_touch",
        title: label,
        meta: "No recent feedback",
        description:
          "This active player has not received a recent player-facing touch.",
        tone: "flag",
        playerId: row.player_id,
        action: {
          kind: "roster",
          playerId: row.player_id,
          context: "no_recent_feedback",
        },
      };
    });

  const followUpCards = buildFollowUpCards(activePlayers, engagement).slice(
    0,
    MAX_CARDS_PER_LANE,
  );

  const unreviewedAssignedItems = engagement?.unreviewed_assigned_items ?? [];
  const sharedCards = unreviewedAssignedItems
    .filter((item) => item.kind !== "playlist")
    .slice(0, MAX_CARDS_PER_LANE)
    .map((item): CoachFeedbackBoardCard => {
      const playerCount = item.player_ids?.length ?? 0;
      return {
        id: `unreviewed-${item.kind}-${item.item_id}`,
        lane: "shared",
        title: item.title?.trim() || fallbackItemTitle(item),
        meta: [
          item.kind,
          playerCount ? `${playerCount} player${playerCount === 1 ? "" : "s"}` : "",
          item.date ?? "",
        ]
          .filter(Boolean)
          .join(" · "),
        description:
          "Shared with a player or family account, but not reviewed yet.",
        tone: "accent",
        playerId: item.player_ids?.[0],
        action: sharedFallbackAction(item),
      };
    });

  const staleGoalCards = goals
    .filter((goal) => isActiveGoal(goal) && activePlayersById.has(goal.player_id))
    .filter((goal) => goal.needs_coach_follow_up || isStaleGoal(goal))
    .sort((a, b) => String(a.target_date).localeCompare(String(b.target_date)))
    .slice(0, MAX_CARDS_PER_LANE)
    .map((goal): CoachFeedbackBoardCard => {
      const player = activePlayersById.get(goal.player_id);
      return {
        id: `goal-${goal.id}`,
        lane: "follow_up",
        title: player ? playerLabel(player) : goal.title,
        meta: goal.needs_coach_follow_up
          ? "Goal needs follow-up"
          : "Goal stale",
        description: goal.title,
        tone: "flag",
        playerId: goal.player_id,
        action: {
          kind: "roster",
          playerId: goal.player_id,
          context: "stale_goal",
        },
      };
    });

  const missingRecapCards = matches
    .filter((match) => match.date && !summaryMatchIds.has(match.id))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, MAX_CARDS_PER_LANE)
    .map((match): CoachFeedbackBoardCard => {
      const label = `${match.home_team} vs ${match.away_team}`;
      return {
        id: `missing-recap-${match.id}`,
        lane: "drafting",
        title: label,
        meta: match.date || "Match",
        description:
          "No match recap is attached yet. Add a recap if this match needs a team follow-up.",
        tone: "default",
        action: { kind: "summaries", matchId: match.id },
      };
    });

  return {
    summary: {
      needsTouch: needsTouchCards.length,
      followUp: followUpCards.length + staleGoalCards.length,
      unreviewed: unreviewedAssignedItems.length,
      missingRecaps: missingRecapCards.length,
    },
    lanes: [
      {
        id: "needs_touch",
        title: "Needs touch",
        description: "Players with no recent feedback or an obvious gap.",
        cards: needsTouchCards,
      },
      {
        id: "follow_up",
        title: "Follow-up",
        description: "Player responses and signals that ask for coach action.",
        cards: [...followUpCards, ...staleGoalCards].slice(
          0,
          MAX_CARDS_PER_LANE,
        ),
      },
      {
        id: "shared",
        title: "Shared",
        description: "Feedback already sent but not reviewed yet.",
        cards: sharedCards,
      },
      {
        id: "drafting",
        title: "Recaps needed",
        description: "Recent matches without a team recap yet.",
        cards: missingRecapCards,
      },
    ],
  };
}

function isActiveGoal(goal: PlayerGoal): boolean {
  return goal.status !== "achieved" && goal.status !== "archived";
}

function isStaleGoal(goal: PlayerGoal): boolean {
  if (!goal.target_date) return false;
  const target = Date.parse(goal.target_date);
  return Number.isFinite(target) && target < Date.now();
}

function buildFollowUpCards(
  activePlayers: Player[],
  engagement?: CoachEngagement,
): CoachFeedbackBoardCard[] {
  const cards: CoachFeedbackBoardCard[] = [];

  for (const player of activePlayers) {
    const needHelp = player.signal_summary?.need_help ?? 0;
    const triedThis = player.signal_summary?.tried_this ?? 0;
    if (needHelp > 0) {
      cards.push({
        id: `signal-need-help-${player.id}`,
        lane: "follow_up",
        title: playerLabel(player),
        meta: `${needHelp} need-help response${needHelp === 1 ? "" : "s"}`,
        description:
          "A linked player or guardian asked for help on recent feedback.",
        tone: "flag",
        playerId: player.id,
        action: {
          kind: "roster",
          playerId: player.id,
          context: "need_help",
        },
      });
    }
    if (triedThis > 0) {
      cards.push({
        id: `signal-tried-this-${player.id}`,
        lane: "follow_up",
        title: playerLabel(player),
        meta: `${triedThis} tried-this response${triedThis === 1 ? "" : "s"}`,
        description:
          "The player says they worked on feedback. Consider closing the loop.",
        tone: "good",
        playerId: player.id,
        action: {
          kind: "roster",
          playerId: player.id,
          context: "tried_this",
        },
      });
    }
  }

  // Phase B: typed, attributed player responses from the backend
  // (column-first signal, clips included) — no client-side
  // `[SIGNAL:*]` decode. need_help + tried_this responses on notes/
  // clips land in the follow-up lane.
  for (const [idx, response] of (
    engagement?.player_responses ?? []
  ).entries()) {
    if (response.signal !== "need_help" && response.signal !== "tried_this") {
      continue;
    }
    if (response.item_kind !== "note" && response.item_kind !== "clip") {
      continue;
    }
    cards.push({
      id: `response-${response.item_kind}-${response.item_id}-${idx}`,
      lane: "follow_up",
      title: signalTitle(response.signal),
      meta: response.reviewed_at ?? "Player response",
      description:
        response.body || "A player response needs a coach follow-up.",
      tone: response.signal === "need_help" ? "flag" : "good",
      action:
        response.item_kind === "clip"
          ? { kind: "clips", itemId: response.item_id }
          : { kind: "notes", itemId: response.item_id },
    });
  }

  return cards;
}

function playerLabel(player: {
  display_name: string;
  jersey_number?: string | null;
}): string {
  return player.jersey_number
    ? `#${player.jersey_number} ${player.display_name}`
    : player.display_name;
}

function signalTitle(signal: PlayerResponseSignal): string {
  if (signal === "need_help") return "Need-help response";
  if (signal === "tried_this") return "Tried-this response";
  return "Player response";
}

function fallbackItemTitle(item: EngagementListItem): string {
  return `${item.kind} ${item.item_id}`;
}

function sharedFallbackAction(
  item: EngagementListItem,
): CoachFeedbackBoardAction {
  const playerId = item.player_ids?.[0];
  if (playerId) {
    return {
      kind: "roster",
      playerId,
      context: "unreviewed_feedback",
    };
  }
  if (item.kind === "clip") return { kind: "clips", itemId: item.item_id };
  if (item.kind === "summary") {
    return {
      kind: "recaps",
      itemId: item.item_id,
      matchId: item.match_id ?? undefined,
    };
  }
  return { kind: "notes", itemId: item.item_id };
}
